import HttpClient from "../../../../core/HttpClient";
import { Authentication } from "@leedpulse-monorepo/classes";
import type { LastCallEntry } from "../overview/getOverviewStats";

const SUMMARY_PENDING_WINDOW_MS = 10 * 60 * 1000;

/**
 * Même fenêtre que l'app : rien à relancer sur un appel manqué, sans durée, ou
 * fini depuis moins de 10 min (le traitement normal tourne encore). Le backend
 * reste l'autorité (messagerie, enregistrement absent).
 */
type RetryableCall = Pick<LastCallEntry, "created" | "duration" | "missed" | "confidential">;

const isAnsweredCall = (call: RetryableCall): boolean =>
    !call.confidential && !call.missed && (call.duration ?? 0) > 0;

export const canRetryCallSummary = (call: RetryableCall, now = Date.now()): boolean =>
    isAnsweredCall(call) && now - (call.created + (call.duration ?? 0) * 1000) > SUMMARY_PENDING_WINDOW_MS;

// `recorded: false` : l'IA a jugé l'appel non enregistré, le backend a masqué l'enregistrement
const readRetryResult = (data?: { summary?: string | null; recorded?: boolean }) => ({
    summary: data?.summary ?? null,
    recorded: data?.recorded !== false,
});

/**
 * Relance la transcription et le résumé d'un appel resté sans résumé (même
 * route que le bouton « Réessayer » de l'app). Le staff passe sur l'appel de
 * n'importe quel client (`CallVoter.canReadCall`).
 *
 * Volontairement sans `catch` : le backend dit pourquoi il refuse (résumé déjà
 * là, relance en cours, pas d'enregistrement, messagerie) et son message est
 * celui à afficher.
 */
async function retryCallTranscription(
    auth: Authentication | null,
    docId: string,
): Promise<{ summary: string | null; recorded: boolean }> {
    if (!auth) throw new Error('Vous devez être connecté pour effectuer cette action.');

    const json = await HttpClient.post(`/calls/${encodeURIComponent(docId)}/retry-transcription`, {}, auth.token);
    return readRetryResult(json?.data);
}

export default retryCallTranscription;
